import DataTable from "../../components/common/DataTable";
import { Eye } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Paper, Group, Loader, Badge, Text, Title, SimpleGrid, ActionIcon } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Head from "../../components/common/Head";
import { getCreatorRevenue } from "../../utils/api";

const CreatorRevenue = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = React.useState(() => {
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");
    if (startDateParam && endDateParam) {
      return [dayjs(startDateParam), dayjs(endDateParam)];
    }
    return [dayjs().subtract(1, "month"), dayjs()];
  });

  const startDate =
    searchParams.get("startDate") || dateRange[0].format("YYYY-MM-DD");
  const endDate =
    searchParams.get("endDate") || dateRange[1].format("YYYY-MM-DD");

  const {
    data: revenueData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["creatorRevenue", id, startDate, endDate],
    queryFn: async () => {
      const [success, data] = await getCreatorRevenue(id, startDate, endDate);
      if (!success) {
        notifications.show({ message: data?.message || "Failed to fetch revenue data", color: "red" });
        throw new Error(data?.message || "Failed to fetch revenue data");
      }
      return data;
    },
    enabled: !!id,
  });

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const dStart = dayjs(dates[0]);
      const dEnd = dayjs(dates[1]);
      setDateRange([dStart, dEnd]);
      setSearchParams({ startDate: dStart.format("YYYY-MM-DD"), endDate: dEnd.format("YYYY-MM-DD") });
    }
  };

  const formatDate = (dateString) => dayjs(dateString).format("MMM DD, YYYY");

  const getStatusColor = (status) => {
    switch (status) {
      case "Paid": return "green";
      case "Pending": return "yellow";
      default: return "red";
    }
  };

  const invoiceColumns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (text) => <Text c="blue" size="sm">{text}</Text>,
    },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      render: (date) => formatDate(date),
    },
    {
      title: "Amount",
      dataIndex: "amountAfterTax",
      key: "amountAfterTax",
      render: (amount, record) => (
        <div>
          <Text size="sm">{amount?.toLocaleString()}Tsh</Text>
          <Text size="xs" c="dimmed">Before Tax: {record.amountBeforeTax?.toLocaleString()}Tsh</Text>
        </div>
      ),
    },
    {
      title: "Taxes",
      dataIndex: "taxes",
      key: "taxes",
      render: (taxes) => (
        <div>
          <Text size="xs">VAT: {taxes?.vat?.percentage || 0}%</Text>
          <Text size="xs">WHT: {taxes?.wht?.percentage || 0}%</Text>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Badge color={getStatusColor(status)} variant="light">{status}</Badge>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <ActionIcon
          variant="subtle"
          color="blue"
          onClick={() => navigate(`/invoices/view/${record._id}`)}
        >
          <Eye size={16} />
        </ActionIcon>
      ),
    },
  ];

  const revenueColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => formatDate(date),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => `${amount?.toLocaleString()}Tsh`,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <Text c="red">Error loading revenue data. Please try again.</Text>
      </div>
    );
  }

  if (!revenueData) {
    return (
      <div className="flex items-center justify-center py-16">
        <Text c="dimmed">No data available</Text>
      </div>
    );
  }

  return (
    <>
      <Head title="Creator Revenue" />
      <div style={{ paddingBlock: "24px" }}>
        <Paper withBorder p="md" mb="lg">
          <Group justify="space-between" align="center" wrap="wrap" gap="md">
            <Title order={4} style={{ margin: 0 }}>Revenue Overview</Title>
            <DatePickerInput
              type="range"
              value={[dateRange[0].toDate(), dateRange[1].toDate()]}
              onChange={handleDateRangeChange}
              style={{ minWidth: 240 }}
            />
          </Group>
        </Paper>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="lg">
          <Paper withBorder p="md">
            <Text size="xs" c="dimmed" mb={4}>Total Revenue</Text>
            <Text size="xl" fw={700} c="blue">{(revenueData.revenueAmount || 0).toLocaleString()} Tsh</Text>
          </Paper>
          <Paper withBorder p="md">
            <Text size="xs" c="dimmed" mb={4}>Available for Payout</Text>
            <Text size="xl" fw={700} c="green">{(revenueData.availableForPayout || 0).toLocaleString()} Tsh</Text>
          </Paper>
          <Paper withBorder p="md">
            <Text size="xs" c="dimmed" mb={4}>Referral Earnings</Text>
            <Text size="xl" fw={700} c="violet">{(revenueData.referralEarning || 0).toLocaleString()} Tsh</Text>
          </Paper>
          <Paper withBorder p="md">
            <Text size="xs" c="dimmed" mb={4}>Total Invoices</Text>
            <Text size="xl" fw={700} c="orange">{revenueData.invoices?.length || 0}</Text>
          </Paper>
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="md">
          <Paper withBorder p="md" style={{ gridColumn: "span 2" }}>
            <Text fw={600} mb="md">Recent Invoices</Text>
            <DataTable
              columns={invoiceColumns}
              dataSource={revenueData.invoices || []}
              rowKey="_id"
              pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} invoices` }}
              scroll={{ x: true }}
            />
          </Paper>
          <Paper withBorder p="md" style={{ position: "sticky", top: 24 }}>
            <Text fw={600} mb="md">Revenue Breakdown</Text>
            <DataTable
              columns={revenueColumns}
              dataSource={revenueData.revenue || []}
              rowKey="_id"
              pagination={{ pageSize: 5, showSizeChanger: false }}
              size="small"
            />
          </Paper>
        </SimpleGrid>
      </div>
    </>
  );
};

export default CreatorRevenue;
